import _ from 'lodash';
import { useEffect, useState } from 'react';
import ApiService from '../../services/ApiService';
import PaymentSaleForm from './PaymentSaleForm';
import {
  CardTransaction,
  CashTransaction,
  PaymentDepartment,
  PaymentSession,
  PaymentSessionCancelResponse,
  PaymentSessionSuccessResponse,
  Invoice,
} from './types';
import LoadingPage from '../LoadingPage';

interface Props {
  amount: number;
  cashInvoicePrinterId?: string;
  customerNum: string;
  department: PaymentDepartment;
  invoiceNum: string;
  onCancel: (response: PaymentSessionCancelResponse) => void;
  onResumeLater?: () => void;
  onSuccess: (response: PaymentSessionSuccessResponse) => void;
  originalInvoiceNum: string;
  selectCashInvoicePrinter?: boolean;
  userInitials: string;
}

const PaymentCreditForm = ({
  amount,
  cashInvoicePrinterId,
  customerNum,
  department,
  invoiceNum,
  onCancel,
  onResumeLater,
  onSuccess,
  originalInvoiceNum,
  selectCashInvoicePrinter = false,
  userInitials,
}: Props) => {
  const [openCredits, setOpenCredits] = useState<PaymentSession[]>([]);
  const [cardTransactions, setCardTransactions] = useState<CardTransaction[]>([]);
  const [cashTransactions, setCashTransactions] = useState<CashTransaction[]>([]);
  const [sessionId, setSessionId] = useState<string>();

  const [ready, setReady] = useState(false);

  const invoices = [{ invoiceNum, originalInvoiceNum, amount: -Math.abs(amount) }];

  const getOpenCredits = async () => {
    const response = await ApiService.get('/payment/session', { params: { originalInvoiceNum, status: 'open' } });
    return response.data || [];
  };

  const createNewSession = async () => {
    let response = await ApiService.get(`/customer/${customerNum}`);
    const customer = response.data;

    response = await ApiService.post('/payment/session', {
      customerNum,
      department,
      invoices,
      customer,
      userInitials,
    });

    return response.data;
  };

  const getTransactions = async () => {
    // get all cash/card transactions from original invoice
    let response = await ApiService.get('/payment/session', { params: { invoiceNum: originalInvoiceNum } });
    let cardTxs: CardTransaction[] = [];
    let cashTxs: CashTransaction[] = [];
    let sessions: PaymentSession[] = [];
    let anySessionId;

    sessions = response.data;
    if (sessions && sessions.length) {
      for (const session of sessions) {
        if (!anySessionId) {
          anySessionId = session.id;
        }
        cardTxs = [...cardTxs, ...session.CardTransactions];
        cashTxs = [...cashTxs, ...session.CashTransactions];
      }
    }

    // get any refunds from credit invoice
    response = await ApiService.get('/payment/session', { params: { invoiceNum } });
    sessions = response.data;
    if (sessions && sessions.length) {
      for (const session of sessions) {
        if (!anySessionId) {
          anySessionId = session.id;
        }
        cardTxs = [...cardTxs, ...session.CardTransactions];
        cashTxs = [...cashTxs, ...session.CashTransactions];
      }
    }

    // hack, create zero cash transaction if none exist to enable cash refunds
    if (!cashTxs.length && anySessionId) {
      const response = await ApiService.post('/payment/cash', { sessionId: anySessionId, amount: 0, userInitials });
      cashTxs = [...cashTxs, response.data];
    }

    setCardTransactions(cardTxs);
    setCashTransactions(cashTxs);
  };

  const getSession = async () => {
    const openCredits: PaymentSession[] = await getOpenCredits();

    // open credits found, prompt user to select from a list (hopefully of just one)
    if (openCredits.length) {
      setOpenCredits(openCredits);
      return;
    }

    // no open credits found, create new session
    const session = await createNewSession();
    setSessionId(session.id);
  };

  useEffect(() => {
    const promises = [getSession(), getTransactions()];

    Promise.all(promises).then(() => setReady(true));
  }, []);

  if (!ready) {
    return <LoadingPage />;
  }

  const renderInvoices = (invoices: Invoice[] = []) => {
    return invoices.map((invoice: Invoice) => {
      return (
        <>
          <div>Credit Invoice: {invoice.invoiceNum}</div>
          <div>Original Invoice: {invoice.originalInvoiceNum}</div>
          <div>Amount: {invoice.amount}</div>
        </>
      );
    });
  };

  const handleResumeSession = (session: PaymentSession) => {
    setSessionId(session.id);
  };

  const startNewSession = async () => {
    const session = await createNewSession();
    setSessionId(session.id);
  };

  if (!sessionId && openCredits.length) {
    /* As of 7/16/21, we only allow 1 open credit invoice attempt at a time now via PIN1.
       If the user presses "Start New Credit" with the logic below rather than resuming the
       existing attempt, it makes extraneous sessions that are left open indefinitely.
       I can't think of a reason why we'd ever need that.
    */
    handleResumeSession(openCredits[0]);
  }

  return (
    <PaymentSaleForm
      action="credit"
      amount={-Math.abs(amount)}
      cardTransactions={cardTransactions}
      cashTransactions={cashTransactions}
      cashInvoicePrinterId={cashInvoicePrinterId}
      customerNum={customerNum}
      department={department}
      invoices={invoices}
      onCancel={onCancel}
      onResumeLater={onResumeLater}
      onSuccess={onSuccess}
      selectCashInvoicePrinter={selectCashInvoicePrinter}
      sessionId={sessionId}
      userInitials={userInitials}
    />
  );
};

export default PaymentCreditForm;
